/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { SurveyListClasses, SurveyListDropdown } from './SurveyListDropdown';
import { selectDefaultSurvey, selectList, selectWithDefault } from '../../slice/initiativeSurveyListSlice';
import { SurveyListItem } from '../../types/survey';
import { useAppSelector } from '../../reducers';

export interface ReportSwitcherDropdownProps {
  initiativeId: string;
  selectedSurveyId: string | undefined;
  disabled?: boolean;
  onChange: (surveyId: string) => void;
  showAutomaticReport?: boolean;
  defaultToCurrent?: boolean;
  surveyList?: SurveyListItem[];
  classes?: SurveyListClasses;
  getItemDisplayText?: (item: SurveyListItem) => string;
}

export const ReportSwitcherDropdown = (props: ReportSwitcherDropdownProps) => {
  const {
    onChange,
    defaultToCurrent = true,
    showAutomaticReport = true,
    disabled,
    initiativeId,
    selectedSurveyId,
    surveyList,
    classes,
  } = props;

  const listState = useAppSelector(selectList);
  const defaultList = useAppSelector(selectWithDefault);
  const defaultSurvey = useAppSelector(selectDefaultSurvey);

  const list = surveyList ?? defaultList;

  const [isUserAction, setUserAction] = useState(false);
  const currentValue = defaultToCurrent ? 'current' : '';

  const handleChange = (item: SurveyListItem) => {
    const newSurveyId = item._id || currentValue;
    onChange(newSurveyId);
    setUserAction(true);
  };

  const selectedItem = useMemo(
    () => list.find((d: SurveyListItem) => d._id === selectedSurveyId) ?? list[0],
    [selectedSurveyId, list]
  );

  const listInitiativeId = listState.initiativeId;
  useEffect(() => {
    // Skip redirect when route initiativeId is different from list initiativeId
    // this should be aligned on the next render
    if (selectedSurveyId === currentValue || initiativeId !== listInitiativeId) {
      return;
    }
    const isSelectedItem = selectedItem && selectedItem._id;
    if (list.length === 0 || isSelectedItem || isUserAction) {
      return;
    }
    if (defaultSurvey) {
      return onChange(defaultSurvey._id);
    }
    return onChange(currentValue);
  }, [
    list,
    selectedItem,
    onChange,
    initiativeId,
    selectedSurveyId,
    isUserAction,
    currentValue,
    defaultSurvey,
    listInitiativeId,
  ]);

  return (
    <SurveyListDropdown
      surveyList={list}
      selectedItem={selectedItem}
      handleDropdownSelect={handleChange}
      showAutomaticReport={showAutomaticReport}
      disabled={disabled}
      classes={classes}
      getItemDisplayText={props.getItemDisplayText}
    />
  );
};
